<template>
  <CModal
    :show="show"
    size="lg"
    :centered="true"
    title="Customer History"
    class="chm-modal"
    @update:show="$emit('update:show', $event)"
  >
    <template #header>
      <div class="chm-head">
        <div class="chm-head__title">
          <span class="chm-head__name">{{ name || 'Customer history' }}</span>
          <a v-if="phone" :href="`tel:${phone}`" class="chm-head__phone">{{ phone }}</a>
        </div>
        <button type="button" class="chm-head__close" aria-label="Close" @click="$emit('update:show', false)">×</button>
      </div>
    </template>

    <div class="chm-body">
      <div v-if="isLoading" class="chm-state">
        <CSpinner size="sm" class="mr-2" />
        <span>Loading history…</span>
      </div>

      <div v-else-if="errorMessage" class="chm-state chm-state--error">{{ errorMessage }}</div>

      <template v-else>
        <!-- Previous Orders -->
        <section class="chm-card">
          <header class="chm-card__head">
            <h3 class="chm-card__title">Previous Orders</h3>
            <span class="chm-card__count">{{ resolvedOrders.length }} total</span>
          </header>
          <div class="chm-table-wrap">
            <table class="chm-table">
              <thead>
                <tr>
                  <th>Order Number</th>
                  <th>Order Date</th>
                  <th class="chm-num">Item Count</th>
                  <th>Order Items</th>
                  <th class="chm-num">Amount</th>
                </tr>
              </thead>
              <tbody>
                <tr v-for="order in resolvedOrders" :key="order.id" :class="{ 'chm-row--recent': recentOrderIdSet.has(Number(order.id)) }">
                  <td class="chm-strong">{{ order.order_number }}</td>
                  <td>{{ formatOrderDate(order.order_date) }}</td>
                  <td class="chm-num">{{ orderItemCount(order) }}</td>
                  <td class="chm-items">{{ orderItemsSummary(order) }}</td>
                  <td class="chm-num chm-amount">{{ formatCurrency(order.grand_total) }}</td>
                </tr>
                <tr v-if="!resolvedOrders.length">
                  <td class="chm-empty-cell" colspan="5">No previous orders.</td>
                </tr>
              </tbody>
            </table>
          </div>
        </section>

        <!-- Previous Calls -->
        <section class="chm-card">
          <header class="chm-card__head">
            <h3 class="chm-card__title">Previous Calls</h3>
            <span class="chm-card__count">{{ resolvedCalls.length }} total</span>
          </header>
          <div class="chm-table-wrap">
            <table class="chm-table">
              <thead>
                <tr>
                  <th>Date</th>
                  <th>Caller</th>
                  <th>Status</th>
                  <th>Comments</th>
                </tr>
              </thead>
              <tbody>
                <tr v-for="(call, idx) in resolvedCalls" :key="`call-${idx}`">
                  <td>{{ formatCallDate(call.date) }}</td>
                  <td>{{ call.caller || 'Unknown caller' }}</td>
                  <td><span :class="['chm-pill', callStatusClass(call.status)]">{{ call.status }}</span></td>
                  <td class="chm-comments">{{ call.comments || '—' }}</td>
                </tr>
                <tr v-if="!resolvedCalls.length">
                  <td class="chm-empty-cell" colspan="4">No previous calls.</td>
                </tr>
              </tbody>
            </table>
          </div>
        </section>
      </template>
    </div>

    <template #footer>
      <button type="button" class="chm-btn chm-btn--ghost" @click="$emit('update:show', false)">Close</button>
    </template>
  </CModal>
</template>

<script>
import axios from 'axios'

export default {
  name: 'CustomerHistoryModal',
  props: {
    show: { type: Boolean, default: false },
    phone: { type: String, default: '' },
    name: { type: String, default: '' },
    // When `provided` is true the parent has already fetched the history
    // (e.g. TeleSales/OrderForm after a phone search) and passes it in via
    // `calls`/`orders` — the modal renders those directly and skips the
    // network call. When false (CallQueue) the modal self-fetches by phone.
    provided: { type: Boolean, default: false },
    calls: { type: Array, default: null },
    orders: { type: Array, default: null },
    // Order IDs the parent considers "recent" (e.g. placed in the last few
    // days in the current org) — their rows render red as a duplicate-order
    // warning. Empty by default, so self-fetch consumers highlight nothing.
    recentOrderIds: { type: Array, default: () => [] }
  },
  data () {
    return {
      isLoading: false,
      errorMessage: '',
      fetchedCalls: [],
      fetchedOrders: [],
      // Remember the phone we last fetched so reopening the same customer
      // doesn't trigger a redundant network call.
      loadedPhone: ''
    }
  },
  computed: {
    // Render from parent-supplied data in provided-mode, otherwise from
    // what this component fetched itself.
    resolvedCalls () {
      return this.provided ? (this.calls || []) : this.fetchedCalls
    },
    resolvedOrders () {
      return this.provided ? (this.orders || []) : this.fetchedOrders
    },
    recentOrderIdSet () {
      return new Set((this.recentOrderIds || []).map(Number))
    }
  },
  watch: {
    show (open) {
      if (open && !this.provided) this.maybeLoad()
    },
    phone () {
      if (this.show && !this.provided) this.maybeLoad()
    }
  },
  methods: {
    maybeLoad () {
      if (!this.phone) {
        this.fetchedCalls = []
        this.fetchedOrders = []
        this.errorMessage = 'No phone number for this contact.'
        return
      }
      // Skip refetch when the same customer is reopened.
      if (this.phone === this.loadedPhone && !this.errorMessage) return
      this.fetchHistory()
    },
    fetchHistory () {
      this.isLoading = true
      this.errorMessage = ''
      axios.get(`${this.$apiAdress}/api/contacts/phone/${encodeURIComponent(this.phone)}?cross_org=1`)
        .then((response) => {
          const data = response.data || {}
          this.fetchedCalls = Array.isArray(data.calls) ? data.calls : []
          this.fetchedOrders = Array.isArray(data.orders) ? data.orders : []
          this.loadedPhone = this.phone
        })
        .catch((error) => {
          console.log(error)
          this.errorMessage = error?.response?.data?.message || 'Unable to load history for this customer.'
        })
        .finally(() => {
          this.isLoading = false
        })
    },
    orderItemCount (order) {
      const items = Array.isArray(order && order.order_items) ? order.order_items : []
      if (items.length) {
        return items.reduce((sum, item) => sum + Number(item.quantity || 0), 0)
      }
      return Number(order && order.item_count) || items.length
    },
    orderItemsSummary (order) {
      const items = Array.isArray(order && order.order_items) ? order.order_items : []
      if (!items.length) return '—'
      return items.map((item) => `${item.name} x ${Number(item.quantity || 0)}`).join(', ')
    },
    // ── Formatters (mirrors TeleSales.vue so the look matches) ──
    formatCurrency (value) {
      const numeric = Number(value || 0)
      return Number.isFinite(numeric) ? `₹${numeric.toFixed(2)}` : '₹0.00'
    },
    formatCallDate (date) {
      if (!date) return ''
      const parts = String(date).split('-')
      if (parts.length !== 3) return String(date)
      const months = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec']
      const day = Number(parts[0])
      const month = months[Number(parts[1]) - 1] || parts[1]
      return `${day} ${month} ${parts[2]}`
    },
    formatOrderDate (iso) {
      if (!iso) return ''
      const d = new Date(iso)
      if (isNaN(d.getTime())) return String(iso)
      const months = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec']
      return `${d.getDate()} ${months[d.getMonth()]} ${d.getFullYear()}`
    },
    formatLabel (value) {
      return String(value || '').replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase())
    },
    callStatusClass (status) {
      const s = String(status || '').toLowerCase()
      if (s.includes('call again') && s.includes('high')) return 'is-danger'
      if (s.includes('sold') || s === 'closed') return 'is-success'
      if (s.includes('not interested')) return 'is-muted'
      if (s.includes('not attended')) return 'is-warn'
      if (s.includes('feedback')) return 'is-info'
      if (s.includes('follow')) return 'is-info'
      return 'is-default'
    },
    paymentStatusClass (status) {
      const s = String(status || '').toLowerCase()
      if (s === 'paid') return 'text-success'
      if (s.includes('pending')) return 'text-warning'
      return ''
    }
  }
}
</script>

<style scoped>
.chm-modal >>> .modal-content {
  border-radius: 14px;
  border: 1px solid #e5e9f2;
  box-shadow: 0 16px 40px rgba(15, 23, 42, 0.22);
  overflow: hidden;
}

.chm-modal >>> .modal-header {
  background: #ffffff;
  border-bottom: 1px solid #eef1f7;
  padding: 0.85rem 1.15rem;
}

.chm-modal >>> .modal-body {
  background: #fbfcfe;
  padding: 1.1rem;
  max-height: calc(100vh - 220px);
  overflow-y: auto;
}

.chm-modal >>> .modal-footer {
  background: #ffffff;
  border-top: 1px solid #eef1f7;
  padding: 0.7rem 1.15rem;
}

.chm-head {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 0.75rem;
  width: 100%;
}

.chm-head__title {
  display: flex;
  flex-direction: column;
  min-width: 0;
}

.chm-head__name {
  font-size: 1.05rem;
  font-weight: 700;
  color: #1f2a44;
  line-height: 1.2;
}

.chm-head__phone {
  font-size: 0.8rem;
  font-weight: 600;
  color: #0e5b46;
  text-decoration: none;
}

.chm-head__phone:hover {
  text-decoration: underline;
}

.chm-head__close {
  background: none;
  border: none;
  font-size: 1.4rem;
  line-height: 1;
  color: #94a3b8;
  cursor: pointer;
  padding: 0 0.25rem;
}

.chm-head__close:hover {
  color: #1f2a44;
}

.chm-head__close:focus { outline: none; }
.chm-head__close:focus-visible {
  outline: none;
  box-shadow: 0 0 0 3px rgba(22, 160, 122, 0.22);
  border-radius: 6px;
}

.chm-state {
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 2rem 1rem;
  font-size: 0.85rem;
  color: #6b7280;
}

.chm-state--error {
  color: #b91c1c;
}

/* ── History cards + tables ── */
.chm-card {
  background: #ffffff;
  border: 1px solid #e6eaf3;
  border-radius: 12px;
  overflow: hidden;
  box-shadow: 0 2px 6px rgba(15, 23, 42, 0.04);
}

.chm-card + .chm-card {
  margin-top: 1rem;
}

.chm-card__head {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 0.75rem;
  padding: 0.75rem 1rem;
  background: #fbfcfe;
  border-bottom: 1px solid #eef1f7;
}

.chm-card__title {
  margin: 0;
  font-size: 0.95rem;
  font-weight: 700;
  color: #1f2a44;
}

.chm-card__count {
  font-size: 0.72rem;
  font-weight: 600;
  color: #0e5b46;
  background: #dff5ea;
  padding: 0.18rem 0.55rem;
  border-radius: 999px;
  white-space: nowrap;
}

.chm-table-wrap {
  max-height: 320px;
  overflow: auto;
}

.chm-table {
  width: 100%;
  border-collapse: collapse;
  font-size: 0.82rem;
}

.chm-table thead th {
  position: sticky;
  top: 0;
  z-index: 1;
  text-align: left;
  font-size: 0.72rem;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.03em;
  color: #6b7280;
  background: #f5f7fb;
  padding: 0.6rem 0.85rem;
  border-bottom: 1px solid #e6eaf3;
  white-space: nowrap;
}

.chm-table tbody td {
  padding: 0.6rem 0.85rem;
  border-bottom: 1px solid #f1f4f9;
  color: #334155;
  vertical-align: top;
}

.chm-table tbody tr:last-child td {
  border-bottom: none;
}

.chm-table tbody tr:nth-child(even) td {
  background: #fbfcfe;
}

.chm-table tbody tr:hover td {
  background: #f5fbf8;
}

/* Recent order → whole row red (duplicate-order warning). Placed after the
   zebra/hover rules so it overrides them regardless of row position. */
.chm-table tbody tr.chm-row--recent td,
.chm-table tbody tr.chm-row--recent:nth-child(even) td,
.chm-table tbody tr.chm-row--recent:hover td {
  background: #d9534f;
  color: #ffffff;
  border-bottom-color: rgba(255, 255, 255, 0.25);
}

.chm-table tbody tr.chm-row--recent td.chm-strong,
.chm-table tbody tr.chm-row--recent td.chm-amount {
  color: #ffffff;
}

.chm-strong {
  font-weight: 700;
  color: #1f2a44;
  white-space: nowrap;
}

.chm-num {
  text-align: right;
  white-space: nowrap;
}

.chm-amount {
  font-weight: 700;
  color: #0e5b46;
}

.chm-items {
  min-width: 160px;
  color: #4b5563;
}

.chm-comments {
  min-width: 160px;
  color: #6b7280;
  white-space: pre-wrap;
  word-break: break-word;
}

.chm-empty-cell {
  text-align: center;
  color: #6b7280;
  font-style: italic;
  padding: 1.2rem 0.85rem;
}

.chm-pill {
  display: inline-flex;
  align-items: center;
  padding: 0.15rem 0.5rem;
  border-radius: 999px;
  font-size: 0.7rem;
  font-weight: 600;
  white-space: nowrap;
}

.chm-pill.is-success { background: #dff5ea; color: #0e5b46; }
.chm-pill.is-info    { background: #e0edff; color: #1e40af; }
.chm-pill.is-warn    { background: #fef3c7; color: #92400e; }
.chm-pill.is-muted   { background: #f1f5f9; color: #64748b; }
.chm-pill.is-danger  { background: #fee2e2; color: #b91c1c; }
.chm-pill.is-default { background: #eef1f7; color: #4b5563; }

.text-success { color: #0e5b46; font-weight: 600; }
.text-warning { color: #92400e; font-weight: 600; }

.chm-btn {
  height: 34px;
  min-width: 90px;
  padding: 0.45rem 1.1rem;
  border-radius: 8px;
  font-size: 0.85rem;
  font-weight: 600;
  cursor: pointer;
}

.chm-btn--ghost {
  background: #ffffff;
  border: 1px solid #e5e9f2;
  color: #1f2a44;
}

.chm-btn--ghost:hover {
  background: #f8fafc;
}

.chm-btn:focus { outline: none; }
.chm-btn:focus-visible {
  outline: none;
  box-shadow: 0 0 0 3px rgba(22, 160, 122, 0.22);
}

@media (max-width: 575px) {
  .history-grid--two {
    grid-template-columns: 1fr;
  }
}
</style>
